<template>
  <div class="map" ref="mapRoot" :id="id">
    <div v-if="zoomControl" class="zoom flex flex-col items-center justify-center gap-2">
      <Button variant="light" @click="zoomIn" :disabled="!canZoomIn">
        <span class="pi pi-plus"></span>
      </Button>
      <Button variant="light" @click="zoomOut" :disabled="!canZoomOut">
        <span class="pi pi-minus"></span>
      </Button>
    </div>
    <PopupComponent
      v-if="enableGetFeatureInfo && layers.info"
      :map="map"
      :layers="popupLayers"
      :service="featureInfoServiceType"
    />
    <div v-if="tileLoadFailed" class="tile-error-notice">
      <span class="pi pi-exclamation-triangle"></span>
      <span>{{ $t('messages.map.tilesFailed', { layers: failedLayerLabels.join(', ') }) }}</span>
      <Button size="small" severity="warn" @click="retryFailedTiles">
        {{ $t('messages.map.tilesRetry') }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es'
import { Map, View } from 'ol'
import type { Coordinate } from 'ol/coordinate'
import { platformModifierKeyOnly } from 'ol/events/condition'
import { boundingExtent } from 'ol/extent'
import {
  DragPan,
  defaults as defaultInteractions,
  MouseWheelZoom,
} from 'ol/interaction'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useId,
  useTemplateRef,
} from 'vue'
import { useMapInteractions } from '@/composables/useMapInteractions'
import { useMapLayers } from '@/composables/useMapLayers'
import { useMapPosition } from '@/composables/useMapPosition'
import { useMapSync } from '@/composables/useMapSync'
import { drawMap, waitForMapLoaded } from '@/services/map/ExportService'
import {
  TILE_LOAD_FAILED_KEY,
  TILE_LOAD_FAILED_URL_KEY,
  resetTileRetry,
} from '@/services/map/LayerService'
import {
  defaultProjection,
  getProjection,
  registerProjection,
} from '@/services/map/ProjectionService'
import { useMapStore } from '@/stores/map'
import PopupComponent from './PopupComponent.vue'

// Define prop types
type BaseLayerType = {
  id: string
  type: string
  visible?: boolean
  zIndex?: number
  minZoom?: number
  maxZoom?: number
  [key: string]: any
}

const {
  center = [24.7536, 59.437],
  extent,
  bbox,
  zoom = 8,
  minZoom = 1,
  maxZoom = 18,
  zoomControl = false,
  twoFingerPanZoom = false,
  projection = 'EPSG:3857',
  baseLayers = [],
  infoLayers = [],
  overlay,
  baseLayer,
  connectFeaturesCollection = false,
  connectFeaturesMinZoom = 0,
  enableSelectFeature = false,
  enableGetFeatureInfo = false,
  featureInfoServiceType = 'wms',
  zoomToFeatures = false,
  enableHover = false,
} = defineProps<{
  center?: number[]
  extent?: number[]
  bbox?: number[]
  zoom?: number
  minZoom?: number
  maxZoom?: number
  zoomControl?: boolean
  twoFingerPanZoom?: boolean
  projection?: string
  baseLayers?: BaseLayerType[]
  infoLayers?: any[]
  overlay?: any
  baseLayer?: string
  connectFeaturesCollection?: boolean
  connectFeaturesMinZoom?: number
  enableSelectFeature?: boolean
  enableGetFeatureInfo?: boolean
  featureInfoServiceType?: string
  zoomToFeatures?: boolean
  enableHover?: boolean
}>()

const emit = defineEmits<{
  (e: 'update', value: { id: string; center: number[]; zoom: number }): void
  (e: 'ready', value: any): void
}>()

const mapStore = useMapStore()

const mapRoot = useTemplateRef<HTMLElement | null>('mapRoot')
const id = useId()
const map = ref<Map | null>(null)
const featureSource = ref<any>(null)

const handleUpdate = updateData => {
  emit('update', updateData)
}

let timeout = null
let featureZoomTimeout = null

const {
  zoomTo,
  panTo,
  fitTo,
  zoomIn,
  zoomOut,
  fitToLayer,
  emitUpdate,
  cleanup: cleanupPosition,
} = useMapPosition({
  map,
  triggerUpdate: handleUpdate,
})

const triggerUpdate = () => {
  emitUpdate(id)
}

// Setup layer management
const {
  layers,
  overlays,
  initLayerGroups,
  createBaseLayers,
  createLayers,
  createOverlay,
  getLayer,
  changeBaseLayer,
  getLayerGroups,
} = useMapLayers({
  triggerUpdate,
})

// Setup map interactions
const {
  enableConnectFeatures,
  enableClickSelect,
  disableClickSelect,
  enableMouseHover,
  disableMouseHover,
} = useMapInteractions({
  map,
  mapStore,
})

// Initialize map sync with store
const { updateStoreLayers, addFeatures: syncAddFeatures } = useMapSync({
  featureSource,
  layers,
  triggerUpdate,
  fitToLayer,
  changeBaseLayer,
  zoomToFeatures: zoomToFeatures,
})

const currentZoom = computed(() => {
  return map.value.getView().getZoom()
})

const canZoomIn = computed(() => {
  if (!map.value) {
    return false
  }

  if (currentZoom.value >= map.value.getView().getMaxZoom()) {
    return false
  }

  return true
})

const canZoomOut = computed(() => {
  if (!map.value) {
    return false
  }

  if (currentZoom.value <= 4) {
    return false
  }

  return true
})

const layerGroups = computed(() => getLayerGroups())

const popupLayers = computed(() => {
  return layers.value.info ? layers.value.info.getLayers().getArray() : []
})

const normalizeBboxExtent = (value?: number[]): number[] | null => {
  if (!value || !value.length) {
    return null
  }
  if (value.length === 4) {
    return value
  }
  return boundingExtent(value as unknown as Coordinate[])
}

// True when the view was fitted to the bbox prop before the first rendered
// frame. Skips the post-mount zoomTo/panTo/updateBbox animations — they would
// otherwise animate from (or back to) the Estonia-wide default view, firing
// country-scale WMS tile requests that are discarded milliseconds later.
let initialBboxApplied = false

const createMap = (): Map | null => {
  const interactions = twoFingerPanZoom
    ? defaultInteractions({ dragPan: false, mouseWheelZoom: false }).extend([
        new DragPan({
          condition: platformModifierKeyOnly,
        }),
        new MouseWheelZoom({
          condition: platformModifierKeyOnly,
        }),
      ])
    : defaultInteractions()

  // Make sure we have a valid DOM element before creating the map
  const target = mapRoot.value

  if (!target) {
    console.error('[MapComponent] Map root element not found')
    return null
  }

  try {
    const view = new View({
      projection: projection,
      center: center,
      zoom: zoom,
      extent: extent,
      maxZoom: maxZoom,
      minZoom: minZoom,
    })

    // Fit to the target extent BEFORE the map exists, so OpenLayers' first
    // render frame is already at parcel scale. The target element hasn't been
    // measured by the map yet, so the size comes from the DOM (falls back to
    // the post-mount updateBbox path when the container has no size yet).
    const initialExtent = normalizeBboxExtent(bbox)
    if (initialExtent && target.clientWidth > 0 && target.clientHeight > 0) {
      view.fit(initialExtent, {
        padding: [50, 50, 50, 50],
        maxZoom: view.getMaxZoom() - 2,
        size: [target.clientWidth, target.clientHeight],
      })
      initialBboxApplied = true
    }

    return new Map({
      layers: layerGroups.value,
      controls: [],
      interactions,
      target,
      moveTolerance: 2,
      pixelRatio: 2,
      view,
    })
  } catch (error) {
    console.error('[MapComponent] Error creating map:', error)
    return null
  }
}

const setFeatureSource = () => {
  if (!overlay) {
    return null
  }
  const layer = getLayer('overlays', overlay.id)
  if (!layer) {
    return null
  }
  const source = layer.get('conf').clusters
    ? layer.getSource()?.getSource()
    : layer.getSource()

  if (source) {
    featureSource.value = source
  }
}

const initLayerGroup = () => {
  initLayerGroups()

  createBaseLayers(baseLayers, baseLayer, minZoom, maxZoom)
  createLayers('info', infoLayers, minZoom, maxZoom)
  createOverlay(overlay, minZoom, maxZoom)

  // Update store layers
  // updateStoreLayers(props.baseLayers, props.infoLayers, overlays.value)
}

// Add features helper function
const addFeatures = (features: any[]) => {
  syncAddFeatures(features)
}

// Function to update bbox
const updateBbox = newBbox => {
  if (newBbox) {
    if (newBbox.length === 4) {
      fitTo(newBbox)
    } else if (newBbox.length === 5) {
      fitTo(boundingExtent(newBbox as unknown as Coordinate[]))
    }
  }
}

// Function to update overlay
const updateOverlay = newOverlay => {
  if (newOverlay && layers.value.overlays) {
    layers.value.overlays.getLayers().clear()
    createOverlay(newOverlay, minZoom, maxZoom)

    // Update feature source after creating the new overlay layer
    setFeatureSource()

    // Trigger fitToLayer if zoomToFeatures is enabled
    nextTick(() => {
      if (zoomToFeatures && featureSource.value) {
        fitToLayer(featureSource.value)
      } else {
        triggerUpdate()
      }
    })

    // Update store layers
    // updateStoreLayers(props.baseLayers, props.infoLayers, overlays.value)
  }
}

// Function to update info layers
const updateInfoLayers = newInfoLayers => {
  if (newInfoLayers && layers.value.info) {
    layers.value.info.getLayers().clear()
    createLayers('info', newInfoLayers, minZoom, maxZoom)
    watchTileSources()

    // Update store layers using the composable
    // updateStoreLayers(props.baseLayers, props.infoLayers, overlays.value)
  }
}

// Tile-failure surfacing: LayerService retries failed WMS tiles a bounded
// number of times and then flags the source. Without this, a geoserver
// 429/500/timeout leaves the layer silently blank (OL caches the ERROR tile
// and never retries) while the base map renders fine — the helpdesk
// "kitsendusi kaardil ei näita" symptom. The notice names the layers so the
// next report says which upstream, and only VISIBLE layers count: a layer a
// card has switched off (a losing planning tier, RED-293) can hold a stale
// flag from requests that were in flight when it was hidden.
const tileLoadFailed = ref(false)
const failedLayerLabels = ref<string[]>([])
const watchedTileSources = new WeakSet()

// Every raster layer with its own visibility. The layer groups this map
// builds are always visible, so own visibility is the effective one — and
// getAllLayers() is side-effect free, unlike getLayerGroup()
// .getLayerStatesArray(), whose external call left every TileWMS layer
// unrendered (2026-08-28).
const eachTileLayer = (
  callback: (layer: any, source: any, visible: boolean) => void,
) => {
  if (!map.value) {
    return
  }
  map.value.getAllLayers().forEach(layer => {
    const source =
      typeof layer.getSource === 'function' ? layer.getSource() : null
    if (
      source &&
      (typeof source.getTileGrid === 'function' ||
        typeof source.getImage === 'function')
    ) {
      callback(layer, source, layer.getVisible())
    }
  })
}

const syncTileFailure = () => {
  const labels: string[] = []
  eachTileLayer((layer, source, visible) => {
    if (visible && source.get(TILE_LOAD_FAILED_KEY)) {
      labels.push(String(layer.get('title') ?? layer.get('id')))
    }
  })
  failedLayerLabels.value = [...new Set(labels)]
  tileLoadFailed.value = labels.length > 0
}

const watchTileSources = () => {
  eachTileLayer((layer, source) => {
    if (watchedTileSources.has(source)) {
      return
    }
    watchedTileSources.add(source)
    // Follows the flag both ways: a source that healed itself (the
    // planning overlay re-resolving its service id, RED-293) takes the
    // notice down again unless another source is still failing.
    source.on('propertychange', event => {
      if (event.key !== TILE_LOAD_FAILED_KEY) {
        return
      }
      if (source.get(TILE_LOAD_FAILED_KEY) && layer.getVisible()) {
        console.warn('[MapComponent] tile source failed', {
          layer: layer.get('id'),
          url: source.get(TILE_LOAD_FAILED_URL_KEY),
        })
      }
      syncTileFailure()
    })
  })
  syncTileFailure()
}

const retryFailedTiles = () => {
  eachTileLayer((_layer, source) => {
    if (source.get(TILE_LOAD_FAILED_KEY)) {
      resetTileRetry(source)
      source.refresh()
    }
  })
  syncTileFailure()
}

// Public method for exporting.
//
// RED-210: Previously relied on a caller-side `waitFor(350)` to cover the
// updateOverlay/updateBbox → rendercomplete window. That's 350ms × 10 maps of
// dead time even when tiles are already cached.
// RED-212: A 1200ms `rendercomplete` safety ceiling raced cold WMS tile loads
// from the QV download-from-history flow and shipped half-rendered frames.
// Now we:
//   1. `await nextTick()` so any `fitToLayer` scheduled by updateOverlay runs.
//   2. Wait for the view animation to finish — `fitTo()` in useMapPosition
//      uses `duration: 250`, and capturing mid-animation shows the feature
//      tiny / off-screen.
//   3. Hand off to `waitForMapLoaded`, which gates on real tile-source state
//      (Map `loadstart`/`loadend`) plus `rendercomplete`, with an 8s ceiling.
const exportMap = async (
  options: { format?: 'png' | 'jpeg'; quality?: number } = {},
): Promise<string> => {
  if (!map.value) {
    console.warn('[MapComponent] Export called but map is not initialized')
    return ''
  }

  await nextTick()

  const { format = 'png', quality } = options
  const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png'

  // Flush any `view.fit()` animation still in flight (250ms default) before
  // we observe tile-load state — otherwise we'd watch loads against the
  // pre-fit viewport.
  const view = map.value.getView()
  if (view.getAnimating()) {
    await new Promise<void>(resolveAnim => {
      const deadline = Date.now() + 500
      const poll = () => {
        if (!view.getAnimating() || Date.now() >= deadline) {
          resolveAnim()
        } else {
          requestAnimationFrame(poll)
        }
      }
      requestAnimationFrame(poll)
    })
  }

  const olMap = map.value
  if (!olMap) {
    return ''
  }

  const reason = await waitForMapLoaded(olMap, { timeoutMs: 8000 })
  if (reason === 'timeout') {
    console.warn('[MapComponent] map-load timed out — capturing what was painted')
  }

  try {
    const canvas = drawMap(olMap)
    if (!canvas) {
      return ''
    }
    return format === 'jpeg'
      ? canvas.toDataURL(mimeType, quality ?? 0.85)
      : canvas.toDataURL(mimeType)
  } catch (error) {
    console.error('[MapComponent] Export error:', error)
    return ''
  }
}

const debouncedUpdate = debounce(() => {
  triggerUpdate()
}, 300)

// Lifecycle hooks
onMounted(() => {
  if (projection && projection !== defaultProjection) {
    registerProjection(projection)
  }

  try {
    initLayerGroup()
    setFeatureSource()
    map.value = createMap()

    if (!map.value) {
      console.error('[MapComponent] Failed to create map')
      return
    }

    map.value.setLayers(layerGroups.value)

    map.value.on('moveend', debouncedUpdate)

    watchTileSources()

    if (connectFeaturesCollection) {
      enableConnectFeatures(featureSource.value, connectFeaturesMinZoom || 0)
    }

    // Initialize store layers
    // updateStoreLayers(props.baseLayers, props.infoLayers, overlays.value)

    if (enableSelectFeature) {
      enableClickSelect()
    }

    if (enableHover) {
      enableMouseHover()
    }

    if (zoom && !initialBboxApplied) {
      zoomTo(zoom)
    }

    if (zoomToFeatures && featureSource.value) {
      featureZoomTimeout = setTimeout(() => {
        fitToLayer(featureSource.value)
      }, 400)
    }

    if (center && center.length === 2 && !initialBboxApplied) {
      panTo(center)
    }

    // Use the updateBbox function
    if (bbox && bbox.length > 0 && !zoomToFeatures && !initialBboxApplied) {
      updateBbox(bbox)
    }

    triggerUpdate()

    // Force a re-render of the map
    timeout = setTimeout(() => {
      if (map.value) {
        map.value.updateSize()
        emit('ready', map.value)
      }
    }, 200)
  } catch (error) {
    console.error('[MapComponent] Error during map initialization:', error)
  }
})

onBeforeUnmount(() => {
  disableMouseHover()
  disableClickSelect()
  cleanupPosition()

  if (timeout) {
    clearTimeout(timeout)
    timeout = null
  }
  if (featureZoomTimeout) {
    clearTimeout(featureZoomTimeout)
    featureZoomTimeout = null
  }

  if (map.value) {
    map.value.un('moveend', debouncedUpdate)
    map.value.dispose()
    map.value = null
  }
})

defineExpose({
  map,
  layers,
  exportMap,
  addFeatures,
  updateBbox,
  updateOverlay,
  updateInfoLayers,
})
</script>

<style>
  @reference '../../../css/tailwind.css';

  .map {
    width: 100%;
    height: 100%;
    position: relative;
    min-width: 900px;
    @apply dark:brightness-75 dark:saturate-50;
  }
  .map .zoom {
    position: absolute;
    top: 15px;
    left: 15px;
    z-index: 1;
  }

  .map .tile-error-notice {
    position: absolute;
    bottom: 15px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    white-space: nowrap;
    @apply bg-white/90 text-gray-700 shadow-sm dark:bg-gray-800/90 dark:text-gray-200;
  }
  .map .tile-error-notice .pi {
    @apply text-amber-500;
  }

  .map .popup-wrapper,
  .map .popup-target {
    width: 1px;
    height: 1px;
  }
  .map .popup-wrapper .popup {
    width: 380px;
  }
  .map .popup-wrapper .popup .popover-body {
    padding: 0;
    max-height: 280px;
    overflow-y: auto;
  }
</style>
